/**
 * Fallback obrigatório para a terminologia de recorrência no front-end.
 *
 * @type {Readonly<{ grupo: Readonly<{ singular: string, plural: string }> }>}
 */
export const TERMINOLOGIA_RECORRENCIA_FALLBACK = Object.freeze({
  grupo: Object.freeze({
    singular: 'Grupo',
    plural: 'Grupos'
  })
});

/**
 * Verifica se o valor é um objeto simples.
 *
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Normaliza a terminologia recebida da API e aplica o fallback obrigatório.
 *
 * @param {unknown} terminologia
 * @returns {{ grupo: { singular: string, plural: string } }}
 */
function normalizeTerminologia(terminologia) {
  const grupo = {
    singular: TERMINOLOGIA_RECORRENCIA_FALLBACK.grupo.singular,
    plural: TERMINOLOGIA_RECORRENCIA_FALLBACK.grupo.plural
  };

  if (isPlainObject(terminologia) && isPlainObject(terminologia.grupo)) {
    if (typeof terminologia.grupo.singular === 'string' && terminologia.grupo.singular.trim()) {
      grupo.singular = terminologia.grupo.singular.trim();
    }

    if (typeof terminologia.grupo.plural === 'string' && terminologia.grupo.plural.trim()) {
      grupo.plural = terminologia.grupo.plural.trim();
    }
  }

  return { grupo };
}

/**
 * Resolve a camada de labels derivadas para as telas de recorrência/adimplência.
 *
 * @param {unknown} terminologia
 * @returns {{
 *   raw: { grupo: { singular: string, plural: string } },
 *   grupo: { singular: string, plural: string },
 *   labelGrupo: string,
 *   labelGrupos: string,
 *   labelGrupoNoContratante: string,
 *   labelGruposNoContratante: string,
 *   placeholderBuscarGrupo: string,
 *   placeholderSelecionarGrupo: string,
 *   placeholderExemploGrupo: string,
 *   acaoAdicionarGrupo: string,
 *   tituloCadastrarGrupo: string,
 *   quantidadeGruposTexto: (count: number) => string
 * }}
 */
export function getTerminologiaRecorrencia(terminologia) {
  const raw = normalizeTerminologia(terminologia);
  const { grupo } = raw;
  const grupoSingularLower = grupo.singular.toLowerCase();
  const grupoPluralLower = grupo.plural.toLowerCase();

  return {
    raw,
    grupo,
    labelGrupo: grupo.singular,
    labelGrupos: grupo.plural,
    labelGrupoNoContratante: grupo.singular,
    labelGruposNoContratante: grupo.plural,
    placeholderBuscarGrupo: `Buscar ${grupoSingularLower}...`,
    placeholderSelecionarGrupo: `Selecione ${grupoSingularLower}`,
    placeholderExemploGrupo: `ex: ${grupo.singular} A`,
    acaoAdicionarGrupo: `Adicionar ${grupo.singular}`,
    tituloCadastrarGrupo: `Cadastrar ${grupo.singular}`,
    quantidadeGruposTexto: (count) =>
      `${count} ${count === 1 ? grupoSingularLower : grupoPluralLower}`
  };
}
